import { useEffect, useState } from 'react';
import axios from 'axios';
import RichTextArea from '@/components/RichTextArea';
import { API } from '@/App';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useEmailSendConfirmation } from '@/hooks/useEmailSendConfirmation';

/**
 * Inline form for the "Thank You Note to Clubs" wrap-up email.
 * Loads attended clubs + persisted settings + sender options, lets the
 * user compose subject/greeting/body/acknowledgement/sender, and sends
 * through the standard dev-mode safety gate.
 */
export default function ThankYouEmailPanel({ competitionId }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('all');
  const [settings, setSettings] = useState(null);
  const [senderOptions, setSenderOptions] = useState({ default: '', options: [] });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const { gate, ConfirmDialogs } = useEmailSendConfirmation();

  useEffect(() => {
    if (!competitionId) {
      setClubs([]);
      setSettings(null);
      setSenderOptions({ default: '', options: [] });
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [clubsRes, settingsRes, senderRes] = await Promise.all([
          axios.get(`${API}/thank-you-email/${competitionId}/attended-clubs`,
            { withCredentials: true }),
          axios.get(`${API}/thank-you-email/${competitionId}`,
            { withCredentials: true }),
          axios.get(`${API}/thank-you-email/${competitionId}/sender-options`,
            { withCredentials: true }),
        ]);
        setClubs(clubsRes.data.clubs || []);
        setSettings(settingsRes.data);
        setSenderOptions(senderRes.data || { default: '', options: [] });
        setSelectedClub('all');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load Thank You Note settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [competitionId]);

  const update = (patch) => setSettings(prev => ({ ...(prev || {}), ...patch }));

  const persistSettings = async () => {
    if (!competitionId || !settings) return;
    try {
      await axios.put(
        `${API}/thank-you-email/${competitionId}`,
        {
          greeting: settings.greeting || '',
          subject: settings.subject || '',
          body_html: settings.body_html || '',
          acknowledgement: settings.acknowledgement || '',
          sender_key: settings.sender_key || null,
        },
        { withCredentials: true }
      );
    } catch (err) {
      console.warn('Could not persist Thank You Note settings:', err);
    }
  };

  const runSend = async (live) => {
    setSending(true);
    try {
      await persistSettings();
      const res = await axios.post(
        `${API}/thank-you-email/${competitionId}/send`,
        { club_id: selectedClub, live },
        { withCredentials: true, timeout: 180000 }
      );
      const sentCount = (res.data.sent || []).length;
      const skippedCount = (res.data.skipped || []).length;
      if (sentCount) toast.success(`${sentCount} email${sentCount === 1 ? '' : 's'} ${live ? 'sent' : 'sent (dev mode → failsafe)'}`);
      if (skippedCount) toast.warning(`${skippedCount} skipped — see console for reasons`);
      if (skippedCount) console.warn('Skipped:', res.data.skipped);
    } catch (err) {
      const status = err.response?.status;
      let msg;
      if (status === 502 || status === 504 || err.code === 'ECONNABORTED') {
        msg = 'Server is still processing the send — check your inbox in a minute.';
      } else {
        msg = err.response?.data?.detail || err.message || 'Send failed';
      }
      toast.error(msg);
      console.error('Thank You send failed:', err);
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    gate((live) => { runSend(live); });
  };

  if (!competitionId) {
    return (
      <div className="text-sm text-stone-600">
        Select a competition above to compose the Thank You note.
      </div>
    );
  }

  if (loading) {
    return <p className="text-sm text-stone-600" data-testid="thank-you-loading">Loading…</p>;
  }

  if (!settings) return null;

  const deliverable = clubs.filter(c => !c.missing_email);
  const willSendCount = selectedClub === 'all'
    ? deliverable.length
    : deliverable.filter(c => c.club_id === selectedClub).length;

  return (
    <div className="space-y-5" data-testid="thank-you-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-stone-800 mb-2">Thank You Note to Clubs</h3>
          <p className="text-sm text-muted-foreground">
            A short email to send to clubs that attended the competition. Pick a single club or
            send to all attending clubs at once.
          </p>
        </div>
        <button
          type="button"
          className="text-xs text-stone-600 underline hover:text-stone-800 whitespace-nowrap shrink-0 mt-1"
          onClick={() => setResetDialogOpen(true)}
          data-testid="thank-you-reset-all-btn"
        >
          Reset all to defaults
        </button>
      </div>

      {clubs.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
          No clubs have entries recorded for this competition yet — there's no one to thank.
        </div>
      ) : (
        <>
          <div>
            <Label htmlFor="ty-club-select">Club</Label>
            <select
              id="ty-club-select"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              data-testid="thank-you-club-select"
            >
              <option value="all">All Clubs ({clubs.length} clubs)</option>
              {clubs.map(c => (
                <option key={c.club_id} value={c.club_id}>
                  {`${c.name}${c.missing_email ? ' (no email on file)' : ''}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="ty-subject-input">Email Subject</Label>
            <input
              id="ty-subject-input"
              type="text"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={settings.subject || ''}
              onChange={(e) => update({ subject: e.target.value })}
              data-testid="thank-you-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="ty-greeting-input">Greeting</Label>
            <input
              id="ty-greeting-input"
              type="text"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={settings.greeting || ''}
              onChange={(e) => update({ greeting: e.target.value })}
              data-testid="thank-you-greeting-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Body text of the email</Label>
              <button
                type="button"
                className="text-xs text-stone-600 underline hover:text-stone-800"
                onClick={async () => {
                  try {
                    const res = await axios.get(
                      `${API}/thank-you-email/${competitionId}/default-body`,
                      { withCredentials: true }
                    );
                    update({ body_html: res.data.body_html });
                    toast.success('Body reset to default');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to load default text');
                  }
                }}
                data-testid="thank-you-reset-body-btn"
              >
                Reset Default Text
              </button>
            </div>
            <RichTextArea
              value={settings.body_html || ''}
              onChange={(html) => update({ body_html: html })}
              testId="thank-you-body-editor"
            />
          </div>

          <div>
            <Label htmlFor="ty-ack-input">Acknowledgement</Label>
            <input
              id="ty-ack-input"
              type="text"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={settings.acknowledgement || ''}
              onChange={(e) => update({ acknowledgement: e.target.value })}
              data-testid="thank-you-acknowledgement-input"
            />
          </div>

          <SenderNamePicker
            options={senderOptions}
            value={settings.sender_key}
            onChange={(key) => update({ sender_key: key })}
            testIdPrefix="thank-you"
          />

          <div className="rounded-lg border bg-stone-50 px-3 py-2 text-sm text-stone-700">
            <span className="font-semibold">{willSendCount}</span>{' '}
            email{willSendCount === 1 ? '' : 's'} will be sent
            {clubs.some(c => c.missing_email) && (
              <span className="text-amber-700 ml-2">
                ({clubs.filter(c => c.missing_email).length} skipped — no email on file)
              </span>
            )}
            .
          </div>

          <Button
            onClick={(e) => { e.preventDefault(); handleSendClick(); }}
            disabled={sending || willSendCount === 0}
            className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-full"
            data-testid="thank-you-send-btn"
          >
            {sending ? <Mail className="mr-2 h-4 w-4 animate-pulse" /> : <Send className="mr-2 h-4 w-4" />}
            {sending ? 'Sending…' : `Send Thank You Email${willSendCount === 1 ? '' : 's'}`}
          </Button>
        </>
      )}

      <ConfirmDialogs />

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent data-testid="thank-you-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Reset all fields to default values?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Reset every field on this form back to its default? Your saved subject,
              greeting, body, acknowledgement and sender choice for this competition will
              be cleared.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="thank-you-reset-no-btn">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await axios.delete(`${API}/thank-you-email/${competitionId}`,
                    { withCredentials: true });
                  const fresh = await axios.get(`${API}/thank-you-email/${competitionId}`,
                    { withCredentials: true });
                  setSettings(fresh.data);
                  toast.success('All fields reset to defaults');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to reset');
                }
              }}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="thank-you-reset-yes-btn"
            >
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Reusable sender-name picker. Shown as a simple <select> when there are
 * 2+ options, or a read-only display when there's only one. Exported so
 * the Entry Pack and Entry Confirmation dialogs can use the same UI.
 */
export function SenderNamePicker({ options, value, onChange, testIdPrefix = 'sender' }) {
  const opts = options?.options || [];
  const currentKey = value || (opts[0]?.key ?? '');
  if (!opts.length) return null;

  return (
    <div data-testid={`${testIdPrefix}-sender-row`}>
      <Label htmlFor={`${testIdPrefix}-sender-select`}>Sender's name (sign-off)</Label>
      {opts.length === 1 ? (
        <input
          id={`${testIdPrefix}-sender-select`}
          type="text"
          readOnly
          value={opts[0].label}
          className="w-full border rounded-lg px-3 py-2 mt-1 bg-stone-50 text-stone-700"
          data-testid={`${testIdPrefix}-sender-readonly`}
        />
      ) : (
        <select
          id={`${testIdPrefix}-sender-select`}
          className="w-full border rounded-lg px-3 py-2 mt-1"
          value={currentKey}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testIdPrefix}-sender-select`}
        >
          {opts.map(o => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
      )}
      <p className="text-xs text-stone-500 mt-1">
        Appears under the acknowledgement at the foot of the email.
      </p>
    </div>
  );
}
